/**
 * The app's shared class strings: one definition of what a button, an input,
 * a label or a card looks like. They are written in the semantic tokens of
 * `index.css` (`bg-surface`, `text-fg`, `border-line`, `bg-primary` …), which
 * switch with the theme on their own — no `dark:` twins needed.
 *
 * Use these (or `<Button>`) for anything new or touched; the design rules in
 * `.cursor/rules/news-generator-frontend-ui.mdc` explain when to reach for which.
 *
 * Adding classes to one of these goes through `cn()`: when two Tailwind
 * utilities set the same property, the stylesheet order decides, not the class
 * order — `${INPUT} text-lg` stayed `text-sm`. `cn()` (tailwind-merge) drops the
 * earlier of two conflicting classes.
 */

import { extendTailwindMerge } from 'tailwind-merge'

/** The token colours, so tailwind-merge knows `text-fg-muted` is a colour and `text-sm` a size. */
const TOKEN_COLOURS = [
    'canvas',
    'surface',
    'surface-subtle',
    'surface-sunken',
    'sidebar',
    'fg',
    'fg-strong',
    'fg-secondary',
    'fg-muted',
    'line',
    'line-strong',
    'line-input',
    'primary',
    'primary-hover',
    'primary-fg',
    'primary-soft',
    'focus',
    'success',
    'success-soft',
    'warning',
    'warning-soft',
    'danger',
    'danger-fg',
    'danger-soft',
    'info',
    'info-soft',
    'hyperblau',
    'navy',
]

const twMerge = extendTailwindMerge({
    extend: { theme: { color: TOKEN_COLOURS, shadow: ['card', 'raised'] } },
})

/** Join class strings; a later class wins over an earlier one for the same property. */
export function cn(...classes: Array<string | false | null | undefined>): string {
    return twMerge(classes.filter(Boolean).join(' '))
}

export const FOCUS_RING =
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-canvas'

/** For controls inside an `overflow-hidden` box (card headers), where an outer ring would be clipped. */
export const FOCUS_RING_INSET = 'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

/** hkcm.com's buttons are pills; so are ours, every variant and size. */
const BUTTON_BASE = `inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS_RING}`

const BUTTON_VARIANT: Record<ButtonVariant, string> = {
    primary: 'bg-primary text-primary-fg shadow-xs hover:bg-primary-hover disabled:hover:bg-primary',
    secondary: 'border border-line-strong bg-surface text-fg shadow-xs hover:bg-surface-subtle disabled:hover:bg-surface',
    ghost: 'text-fg-secondary hover:bg-surface-subtle hover:text-fg disabled:hover:bg-transparent',
    danger: 'bg-danger text-danger-fg shadow-xs hover:opacity-90 disabled:hover:opacity-50',
}

const BUTTON_SIZE: Record<ButtonSize, string> = {
    sm: 'min-h-8 px-3.5 py-1 text-xs',
    md: 'min-h-10 px-5 py-2 text-sm',
    lg: 'min-h-11 px-6 py-2.5 text-sm',
}

export function buttonClass(variant: ButtonVariant = 'primary', size: ButtonSize = 'md'): string {
    return `${BUTTON_BASE} ${BUTTON_VARIANT[variant]} ${BUTTON_SIZE[size]}`
}

/** A round icon-only button. Always give it an `aria-label`. */
export const ICON_BUTTON = `inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-surface-sunken hover:text-fg disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS_RING}`

export const INPUT =
    'block w-full rounded-lg border border-line-input bg-surface px-3 py-2 text-sm text-fg shadow-xs placeholder:text-fg-muted focus:border-primary focus:outline-hidden focus:ring-3 focus:ring-focus/25 disabled:cursor-not-allowed disabled:opacity-60'

/** A form label: sentence case, above its control. */
export const LABEL = 'mb-1.5 block text-sm font-semibold text-fg-secondary'

/** A hint below a control. */
export const HINT = 'mt-1.5 text-xs text-fg-muted'

export const CARD = 'rounded-2xl border border-line bg-surface shadow-card'

/** The header strip of a card: title left, actions right. */
export const CARD_HEADER = 'flex items-center justify-between gap-3 border-b border-line px-5 py-3'

/** A small uppercase overline above a group of content inside a card or panel. */
export const SECTION_TITLE = 'text-xs font-semibold uppercase tracking-wider text-fg-muted'
